import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Box, Container, Typography, Link, Grid, Divider, 
    useTheme, Stack, IconButton
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Logo from './Logo';

const Footer = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: theme.palette.primary.dark,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                pt: 6,
                pb: 4
            }}
        >
            {/* Decorative Pattern */}
            <Box 
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 5,
                    background: `linear-gradient(90deg, 
                        ${theme.palette.secondary.main} 0%, 
                        ${theme.palette.secondary.light} 50%, 
                        ${theme.palette.secondary.main} 100%)`
                }}
            />

            <Container maxWidth="lg">
                {/* Main Footer Content */}
                <Grid container spacing={4} sx={{ textAlign: { xs: 'center', md: 'start' } }}>
                    {/* Brand Column */}
                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                            <Logo variant="light" size="medium" />
                        </Box>
                        
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'rgba(255,255,255,0.8)', 
                                mb: 3,
                                maxWidth: { xs: '100%', md: 280 },
                                px: { xs: 2, md: 0 } // Add horizontal padding on mobile
                            }}
                        >
                            {t('footer.tagline')}
                        </Typography>

                        <Stack 
                            direction="row" 
                            spacing={1}
                            sx={{ 
                                justifyContent: { xs: 'center', md: 'flex-start' },
                                width: '100%'
                            }}
                        >
                            <IconButton 
                                aria-label="Facebook" 
                                size="small"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        color: theme.palette.secondary.main,
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <FacebookIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                                aria-label="Twitter" 
                                size="small"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        color: theme.palette.secondary.main,
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <TwitterIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                                aria-label="Instagram" 
                                size="small"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        color: theme.palette.secondary.main,
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <InstagramIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                                aria-label="GitHub" 
                                size="small" 
                                component="a"
                                href="https://github.com/imuslimlife/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        color: theme.palette.secondary.main,
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <GitHubIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Grid>
                    
                    {/* Quick Links */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography 
                            variant="subtitle1"
                            component="h2"
                            sx={{ 
                                mb: 2, 
                                color: theme.palette.secondary.main,
                                fontWeight: 'medium',
                                textAlign: { xs: 'center', md: 'start' }
                            }}
                        >
                            {t('footer.quickLinks')}
                        </Typography>
                        
                        <Stack 
                            spacing={1.5} 
                            alignItems={{ xs: 'center', md: 'flex-start' }}
                            width="100%"
                        >
                            <Link 
                                component={RouterLink} 
                                to="/prayer-times" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.prayerTimes')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/calendar" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.calendar')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/quran" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.quran')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/qibla" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.qibla')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/mosque-directory" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.mosqueDirectory')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/mobile-app" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.mobileApp')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/alexa-skill" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.alexaSkill')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/niyyah" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.niyyah')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/tasmi" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.tasmi')}
                            </Link>
                            <Link 
                                href="/alexa-athan/" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.athanOnAlexa')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/ai-agents" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('nav.aiAgents')}
                            </Link>
                        </Stack>
                    </Grid>
                    
                    {/* Resources Column */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography 
                            variant="subtitle1"
                            component="h2"
                            sx={{ 
                                mb: 2, 
                                color: theme.palette.secondary.main,
                                fontWeight: 'medium',
                                textAlign: { xs: 'center', md: 'start' }
                            }}
                        >
                            {t('footer.resources')}
                        </Typography>
                        
                        <Stack 
                            spacing={1.5}
                            alignItems={{ xs: 'center', md: 'flex-start' }}
                            width="100%"
                        >
                            <Link 
                                component={RouterLink} 
                                to="/api" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.apiDocs')}
                            </Link>
                            <Link 
                                href="https://github.com/imuslimlife/" 
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.github')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/faq-support" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.support')}
                            </Link>
                            <Link 
                                component={RouterLink} 
                                to="/feature-request" 
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.featureRequests')}
                            </Link>
                            <Link 
                                href="https://imuslimlife.com/privacy-policy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    color: 'rgba(255,255,255,0.7)',
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    '&:hover': { 
                                        color: 'white',
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                {t('footer.privacyPolicy')}
                            </Link>
                        </Stack>
                    </Grid>
                    
                    {/* Contact Column */}
                    <Grid item xs={12} md={3}>
                        <Typography 
                            variant="subtitle1"
                            component="h2"
                            sx={{ 
                                mb: 2, 
                                color: theme.palette.secondary.main,
                                fontWeight: 'medium',
                                textAlign: { xs: 'center', md: 'start' }
                            }}
                        >
                            {t('footer.contact')}
                        </Typography>
                        
                        <Stack 
                            spacing={2}
                            alignItems={{ xs: 'center', md: 'flex-start' }}
                            width="100%"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                <EmailIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        marginInlineEnd: '8px',
                                        color: theme.palette.secondary.light
                                    }}
                                />
                                <Link 
                                    href="mailto:info@yzinfotech.com"
                                    sx={{ 
                                        color: 'rgba(255,255,255,0.7)',
                                        textDecoration: 'none',
                                        transition: 'color 0.2s',
                                        '&:hover': { 
                                            color: 'white',
                                            textDecoration: 'none'
                                        }
                                    }}
                                >
                                    info@yzinfotech.com
                                </Link>
                            </Box>
                            
                            
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                <LocationOnIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        marginInlineEnd: '8px', 
                                        mt: 0.3,
                                        color: theme.palette.secondary.light,
                                        flexShrink: 0
                                    }}
                                />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    604 S. Hunt Club Blvd.<br />
                                    Apopka, FL 32703<br />
                                    {t('footer.country')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                
                {/* Divider */}
                <Divider 
                    sx={{ 
                        my: 4, 
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.8
                    }} 
                />
                
                {/* Copyright and Legal Info */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: { xs: 'center', sm: 'space-between' }, // Center on mobile, space-between on larger screens
                        flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on mobile
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}
                >
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'rgba(255,255,255,0.6)',
                            textAlign: { xs: 'center', sm: 'start' } // Center text on mobile
                        }}
                    >
                        © {currentYear} iMuslim Life. {t('footer.rights')}
                    </Typography>
                    
                    <Box 
                        sx={{ 
                            mt: { xs: 2, sm: 0 },
                            display: 'flex',
                            gap: 3,
                            justifyContent: 'center' // Always center the links
                        }}
                    >
                        <Link 
                            href="https://imuslimlife.com/privacy-policy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                                color: 'rgba(255,255,255,0.6)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: theme.palette.secondary.main }
                            }}
                        >
                            {t('footer.privacy')}
                        </Link>
                        <Link 
                            href="https://imuslimlife.com/terms/"
                            sx={{ 
                                color: 'rgba(255,255,255,0.6)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: theme.palette.secondary.main }
                            }}
                        >
                            {t('footer.terms')}
                        </Link>
                        <Link 
                            href="#cookies"
                            sx={{ 
                                color: 'rgba(255,255,255,0.6)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: theme.palette.secondary.main }
                            }}
                        >
                            {t('footer.cookies')}
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;